import React, { useState, useEffect, useCallback } from 'react';
import {
    Box,
    Typography,
    TextField,
    Button,
    CircularProgress,
    Alert,
    InputAdornment,
    IconButton,
    Chip,
    Paper,
    Divider,
    Tooltip,
    alpha,
    useTheme,
} from '@mui/material';
import {
    Save as SaveIcon,
    Visibility as VisibilityIcon,
    VisibilityOff as VisibilityOffIcon,
    NetworkCheck as NetworkCheckIcon,
    CheckCircle as CheckCircleIcon,
    Cancel as CancelIcon,
    ContentCopy as CopyIcon,
    Instagram as InstagramIcon,
    YouTube as YouTubeIcon,
    Facebook as FacebookIcon,
} from '@mui/icons-material';
import { adminApi } from '../services/api';

// ── Field definitions ─────────────────────────────────────────────────────────

const INSTAGRAM_FIELDS = [
    {
        key: 'instagram_app_id',
        label: 'App ID',
        placeholder: '1234567890',
        hint: 'Found in Meta Developer Portal → your app → Settings → Basic.',
        secret: false,
    },
    {
        key: 'instagram_app_secret',
        label: 'App Secret',
        placeholder: 'Leave blank to keep existing secret',
        hint: 'Server-side secret. Never share or expose this.',
        secret: true,
    },
];

const FACEBOOK_FIELDS = [
    {
        key: 'facebook_app_id',
        label: 'App ID',
        placeholder: '1234567890',
        hint: 'Found in Meta Developer Portal → your app → Settings → Basic.',
        secret: false,
    },
    {
        key: 'facebook_app_secret',
        label: 'App Secret',
        placeholder: 'Leave blank to keep existing secret',
        hint: 'Server-side secret. Never share or expose this.',
        secret: true,
    },
];

const YOUTUBE_FIELDS = [
    {
        key: 'youtube_client_id',
        label: 'OAuth Client ID',
        placeholder: '123456789-abc....apps.googleusercontent.com',
        hint: 'Found in Google Cloud Console → APIs & Services → Credentials → OAuth 2.0 Client.',
        secret: false,
    },
    {
        key: 'youtube_client_secret',
        label: 'OAuth Client Secret',
        placeholder: 'Leave blank to keep existing secret',
        hint: 'Server-side secret from the same Google Cloud OAuth 2.0 Client.',
        secret: true,
    },
];

const REDIRECT_URI_FALLBACK = `${window.location.origin}/settings`;

// ── Sub-components ────────────────────────────────────────────────────────────

function SecretField({ field, value, showSecrets, onToggle, onChange }) {
    return (
        <Box>
            <TextField
                fullWidth
                label={field.label}
                placeholder={field.placeholder}
                value={value}
                onChange={(e) => onChange(field.key, e.target.value)}
                type={field.secret && !showSecrets ? 'password' : 'text'}
                size="small"
                inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.85rem' } }}
                InputProps={field.secret ? {
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton size="small" onClick={onToggle} edge="end">
                                {showSecrets ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                            </IconButton>
                        </InputAdornment>
                    ),
                } : undefined}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {field.hint}
            </Typography>
        </Box>
    );
}

function RedirectUriBox({ label, uri }) {
    const [copied, setCopied] = useState(false);
    const handleCopy = () => {
        navigator.clipboard.writeText(uri).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };
    return (
        <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                {label}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextField
                    fullWidth
                    value={uri}
                    size="small"
                    InputProps={{ readOnly: true }}
                    inputProps={{ style: { fontFamily: 'monospace', fontSize: '0.8rem' } }}
                />
                <Tooltip title={copied ? 'Copied!' : 'Copy'}>
                    <IconButton size="small" onClick={handleCopy}>
                        <CopyIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>
        </Box>
    );
}

function PlatformCard({
    title, icon, accentColor, fields,
    values, onValueChange,
    showSecrets, onToggleSecrets,
    saving, testing,
    error, success,
    connectionStatus,
    onSave, onTest,
    redirectUriLabel, redirectUri,
}) {
    const theme = useTheme();
    return (
        <Paper
            elevation={0}
            sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderTop: `3px solid ${accentColor}`,
                borderRadius: '12px',
                overflow: 'hidden',
                mb: 3,
            }}
        >
            {/* Header */}
            <Box sx={{ px: 3, py: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{
                    width: 36, height: 36, borderRadius: '8px',
                    bgcolor: alpha(accentColor, 0.12),
                    color: accentColor,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    {icon}
                </Box>
                <Typography variant="subtitle1" fontWeight={700}>{title}</Typography>
                {connectionStatus === true && (
                    <Chip icon={<CheckCircleIcon />} label="Connected" size="small" color="success" sx={{ ml: 'auto' }} />
                )}
                {connectionStatus === false && (
                    <Chip icon={<CancelIcon />} label="Failed" size="small" color="error" sx={{ ml: 'auto' }} />
                )}
            </Box>

            <Divider />

            {/* Body */}
            <Box sx={{ p: 3 }}>
                {redirectUriLabel && (
                    <RedirectUriBox label={redirectUriLabel} uri={redirectUri} />
                )}

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mb: 2.5 }}>
                    {fields.map((field) => (
                        <SecretField
                            key={field.key}
                            field={field}
                            value={values[field.key] || ''}
                            showSecrets={showSecrets}
                            onToggle={onToggleSecrets}
                            onChange={onValueChange}
                        />
                    ))}
                </Box>

                {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
                {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

                <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    <Button
                        variant="contained"
                        startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                        onClick={onSave}
                        disabled={saving || testing}
                        sx={{ bgcolor: accentColor, '&:hover': { bgcolor: alpha(accentColor, 0.85) } }}
                    >
                        {saving ? 'Saving…' : 'Save'}
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={testing ? <CircularProgress size={16} /> : <NetworkCheckIcon />}
                        onClick={onTest}
                        disabled={saving || testing}
                        sx={{ borderColor: accentColor, color: accentColor }}
                    >
                        {testing ? 'Testing…' : 'Test Connection'}
                    </Button>
                </Box>
            </Box>
        </Paper>
    );
}

// ── Initial state helpers ─────────────────────────────────────────────────────

const INITIAL_INSTAGRAM = { instagram_app_id: '', instagram_app_secret: '' };
const INITIAL_FACEBOOK  = { facebook_app_id: '',  facebook_app_secret: '' };
const INITIAL_YOUTUBE   = { youtube_client_id: '', youtube_client_secret: '' };

// ── Main page ─────────────────────────────────────────────────────────────────

export default function AdminPlatformSettings() {
    const [loading,    setLoading]    = useState(true);
    const [pageError,  setPageError]  = useState('');
    const [redirectUri, setRedirectUri] = useState(REDIRECT_URI_FALLBACK);

    const [igValues,   setIgValues]   = useState(INITIAL_INSTAGRAM);
    const [fbValues,   setFbValues]   = useState(INITIAL_FACEBOOK);
    const [ytValues,   setYtValues]   = useState(INITIAL_YOUTUBE);

    const [igShow,     setIgShow]     = useState(false);
    const [fbShow,     setFbShow]     = useState(false);
    const [ytShow,     setYtShow]     = useState(false);

    const [igSaving,   setIgSaving]   = useState(false);
    const [fbSaving,   setFbSaving]   = useState(false);
    const [ytSaving,   setYtSaving]   = useState(false);

    const [igTesting,  setIgTesting]  = useState(false);
    const [fbTesting,  setFbTesting]  = useState(false);
    const [ytTesting,  setYtTesting]  = useState(false);

    const [igError,    setIgError]    = useState('');
    const [fbError,    setFbError]    = useState('');
    const [ytError,    setYtError]    = useState('');

    const [igSuccess,  setIgSuccess]  = useState('');
    const [fbSuccess,  setFbSuccess]  = useState('');
    const [ytSuccess,  setYtSuccess]  = useState('');

    const [igStatus,   setIgStatus]   = useState(null);
    const [fbStatus,   setFbStatus]   = useState(null);
    const [ytStatus,   setYtStatus]   = useState(null);

    // ── Fetch ──────────────────────────────────────────────────────────────────

    const fetchConfig = useCallback(async () => {
        try {
            setLoading(true);
            setPageError('');
            const cfg = await adminApi.getPlatformConfig();
            if (cfg.redirect_uri) setRedirectUri(cfg.redirect_uri);
            setIgValues({
                instagram_app_id:     cfg.instagram_app_id     || '',
                instagram_app_secret: cfg.instagram_app_secret || '',
            });
            setFbValues({
                facebook_app_id:     cfg.facebook_app_id     || '',
                facebook_app_secret: cfg.facebook_app_secret || '',
            });
            setYtValues({
                youtube_client_id:     cfg.youtube_client_id     || '',
                youtube_client_secret: cfg.youtube_client_secret || '',
            });
        } catch (err) {
            setPageError(err?.message || 'Failed to load platform configuration');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchConfig(); }, [fetchConfig]);

    // ── Value change helpers ───────────────────────────────────────────────────

    const handleIgChange  = (k, v) => setIgValues(p => ({ ...p, [k]: v }));
    const handleFbChange  = (k, v) => setFbValues(p => ({ ...p, [k]: v }));
    const handleYtChange  = (k, v) => setYtValues(p => ({ ...p, [k]: v }));

    // ── Save handlers ──────────────────────────────────────────────────────────

    const handleIgSave = async () => {
        setIgSaving(true); setIgError(''); setIgSuccess('');
        try {
            await adminApi.updatePlatformConfig(igValues);
            setIgSuccess('Instagram credentials saved');
            fetchConfig();
        } catch (err) {
            setIgError(err?.message || 'Save failed');
        } finally { setIgSaving(false); }
    };

    const handleFbSave = async () => {
        setFbSaving(true); setFbError(''); setFbSuccess('');
        try {
            await adminApi.updatePlatformConfig(fbValues);
            setFbSuccess('Facebook credentials saved');
            fetchConfig();
        } catch (err) {
            setFbError(err?.message || 'Save failed');
        } finally { setFbSaving(false); }
    };

    const handleYtSave = async () => {
        setYtSaving(true); setYtError(''); setYtSuccess('');
        try {
            await adminApi.updatePlatformConfig(ytValues);
            setYtSuccess('YouTube credentials saved');
            fetchConfig();
        } catch (err) {
            setYtError(err?.message || 'Save failed');
        } finally { setYtSaving(false); }
    };

    // ── Test handlers ──────────────────────────────────────────────────────────

    const handleIgTest = async () => {
        setIgTesting(true); setIgError(''); setIgSuccess('');
        try {
            const res = await adminApi.testInstagramConnection();
            setIgStatus(res.connected);
            if (res.connected) setIgSuccess(res.message);
            else setIgError(res.message);
        } catch (err) {
            setIgStatus(false);
            setIgError(err?.message || 'Test failed');
        } finally { setIgTesting(false); }
    };

    const handleFbTest = async () => {
        setFbTesting(true); setFbError(''); setFbSuccess('');
        try {
            const res = await adminApi.testFacebookConnection();
            setFbStatus(res.connected);
            if (res.connected) setFbSuccess(res.message);
            else setFbError(res.message);
        } catch (err) {
            setFbStatus(false);
            setFbError(err?.message || 'Test failed');
        } finally { setFbTesting(false); }
    };

    const handleYtTest = async () => {
        setYtTesting(true); setYtError(''); setYtSuccess('');
        try {
            const res = await adminApi.testYouTubeConnection();
            setYtStatus(res.connected);
            if (res.connected) setYtSuccess(res.message);
            else setYtError(res.message);
        } catch (err) {
            setYtStatus(false);
            setYtError(err?.message || 'Test failed');
        } finally { setYtTesting(false); }
    };

    // ── Render ─────────────────────────────────────────────────────────────────

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (pageError) {
        return (
            <Box sx={{ maxWidth: 640, mx: 'auto', mt: 6 }}>
                <Alert severity="error" action={
                    <Button size="small" onClick={fetchConfig}>Retry</Button>
                }>{pageError}</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ maxWidth: 680, mx: 'auto', py: 4, px: { xs: 2, md: 0 } }}>
            <Typography variant="h5" fontWeight={800} sx={{ mb: 0.5 }}>
                Platform Credentials
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                Configure the shared OAuth app credentials used for Instagram, Facebook, and YouTube.
                Users will never see these — they just click "Connect" in Settings.
            </Typography>

            <PlatformCard
                title="Instagram"
                icon={<InstagramIcon fontSize="small" />}
                accentColor="#e1306c"
                fields={INSTAGRAM_FIELDS}
                values={igValues}
                onValueChange={handleIgChange}
                showSecrets={igShow}
                onToggleSecrets={() => setIgShow(s => !s)}
                saving={igSaving}
                testing={igTesting}
                error={igError}
                success={igSuccess}
                connectionStatus={igStatus}
                onSave={handleIgSave}
                onTest={handleIgTest}
                redirectUriLabel="OAuth Redirect URI — add this in Meta Developer Portal → your app → Facebook Login → Valid OAuth Redirect URIs"
                redirectUri={redirectUri}
            />

            <PlatformCard
                title="Facebook"
                icon={<FacebookIcon fontSize="small" />}
                accentColor="#1877f2"
                fields={FACEBOOK_FIELDS}
                values={fbValues}
                onValueChange={handleFbChange}
                showSecrets={fbShow}
                onToggleSecrets={() => setFbShow(s => !s)}
                saving={fbSaving}
                testing={fbTesting}
                error={fbError}
                success={fbSuccess}
                connectionStatus={fbStatus}
                onSave={handleFbSave}
                onTest={handleFbTest}
                redirectUriLabel="OAuth Redirect URI — add this in Meta Developer Portal → your app → Facebook Login → Valid OAuth Redirect URIs"
                redirectUri={redirectUri}
            />

            <PlatformCard
                title="YouTube"
                icon={<YouTubeIcon fontSize="small" />}
                accentColor="#ff0000"
                fields={YOUTUBE_FIELDS}
                values={ytValues}
                onValueChange={handleYtChange}
                showSecrets={ytShow}
                onToggleSecrets={() => setYtShow(s => !s)}
                saving={ytSaving}
                testing={ytTesting}
                error={ytError}
                success={ytSuccess}
                connectionStatus={ytStatus}
                onSave={handleYtSave}
                onTest={handleYtTest}
                redirectUriLabel="Authorised Redirect URI — add this in Google Cloud Console → APIs & Services → Credentials → your OAuth 2.0 Client"
                redirectUri={redirectUri}
            />
        </Box>
    );
}
